import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, Mail, Shield } from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { supabase } from "@/integrations/supabase/client";
import { forgotSchema, friendlyAuthError } from "@/lib/auth-validation";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [{ title: "ClaimGuard | Reset password" }],
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldError(null);
    const parsed = forgotSchema.safeParse({ email });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(parsed.data.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (err) throw err;
      setSent(true);
    } catch (err) {
      console.error("[auth] password reset failed:", err);
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        <div className="container-page grid place-items-center py-14">
          <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-sm">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy text-navy-foreground">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight">Forgot your password?</h1>
                <p className="text-sm text-muted-foreground">
                  We&apos;ll email you a link to reset it.
                </p>
              </div>
            </div>

            {sent ? (
              <div className="space-y-4 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-savings/15 text-savings">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <p className="text-sm text-muted-foreground">
                  If an account exists for{" "}
                  <span className="font-medium text-foreground">{email}</span>, a reset link is on
                  the way.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:opacity-90"
                >
                  Back to sign in
                </Link>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                <form onSubmit={onSubmit} className="space-y-4" noValidate>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Email</label>
                    <div
                      className={`flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-navy/20 ${
                        fieldError ? "border-destructive" : "border-input"
                      }`}
                    >
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <input
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-transparent outline-none"
                      />
                    </div>
                    {fieldError && <p className="mt-1 text-xs text-destructive">{fieldError}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex w-full items-center justify-center rounded-md bg-navy px-4 py-2.5 text-sm font-semibold text-navy-foreground hover:opacity-90 disabled:opacity-60"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                  </button>
                </form>
                <div className="mt-6 border-t pt-4 text-center text-sm text-muted-foreground">
                  Remember it?{" "}
                  <Link to="/login" className="font-medium text-navy hover:underline">
                    Sign in
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
